import { MockAIProvider } from "@/ai/mock";
import type {
  ActionResult,
  JiaxingCase,
  KnowledgeCard,
  NextQuestion,
  PublicStats,
  QuestionAnalysis,
  RecallCoordinate,
  ReflectionResult,
  Topic,
} from "@/types/journey";
import { createLocalSessionId } from "@/lib/ids";
import { cleanUserText } from "@/lib/text";
import { eligibleCases, fallbackCaseMatch, type CaseMatchRequest, type CaseMatchResult } from "@/ai/case-match";
import { RECALL_LABELS, fallbackRecallCoordinate, recallFocus, type RecallRequest } from "@/ai/recall";

export type ApiSource = "api" | "deepseek" | "local-mock" | "local-fallback";

function apiSource(source: ApiEnvelope<unknown>["source"]): ApiSource {
  if (source === "deepseek") return "deepseek";
  if (source === "mock") return "local-mock";
  if (source === "fallback") return "local-fallback";
  return "api";
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status = 0,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

interface ApiEnvelope<T> {
  data: T;
  source?: "openai" | "deepseek" | "mock" | "fallback";
}

export interface Sourced<T> {
  value: T;
  source: ApiSource;
}

const localMock = new MockAIProvider();

async function requestEnvelope<T>(path: string, init: RequestInit, timeoutMs = 9000): Promise<ApiEnvelope<T>> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(path, {
      ...init,
      signal: controller.signal,
      headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    });
    if (!response.ok) throw new ApiClientError("REQUEST_FAILED", response.status);
    const payload = (await response.json()) as ApiEnvelope<T> | T;
    if (typeof payload === "object" && payload !== null && "data" in payload) {
      return payload;
    }
    return { data: payload as T };
  } catch (error) {
    if (error instanceof ApiClientError) throw error;
    throw new ApiClientError(error instanceof Error && error.name === "AbortError" ? "REQUEST_TIMEOUT" : "NETWORK_OFFLINE");
  } finally {
    window.clearTimeout(timer);
  }
}

async function request<T>(path: string, init: RequestInit, timeoutMs = 9000): Promise<T> {
  const envelope = await requestEnvelope<T>(path, init, timeoutMs);
  return envelope.data;
}

export class JourneyApi {
  async matchCase(input: CaseMatchRequest): Promise<CaseMatchResult> {
    try {
      const result = await request<CaseMatchResult>("/api/case-match", { method: "POST", body: JSON.stringify(input) }, 28000);
      if (result.item && !eligibleCases([result.item]).length) throw new Error("UNPUBLISHED_CASE");
      return result;
    } catch {
      try {
        const candidates = await request<JiaxingCase[]>("/api/public/cases", { method: "GET" }, 4000);
        return fallbackCaseMatch(input, candidates);
      } catch { return fallbackCaseMatch(input, []); }
    }
  }
  async track(sessionId: string, event: "ticket_generated"): Promise<void> {
    try {
      await request<{ recorded: true }>("/api/event", {
        method: "POST",
        body: JSON.stringify({ sessionId, event }),
      }, 3000);
    } catch {
      // Analytics is deliberately best-effort and never blocks the journey.
    }
  }

  async startSession(): Promise<string> {
    try {
      const result = await request<{ sessionId: string }>("/api/session", {
        method: "POST",
        body: JSON.stringify({}),
      });
      return result.sessionId;
    } catch {
      return createLocalSessionId();
    }
  }

  async analyzeQuestion(sessionId: string, question: string): Promise<Sourced<QuestionAnalysis>> {
    const safeQuestion = cleanUserText(question);
    try {
      const envelope = await requestEnvelope<QuestionAnalysis>("/api/question", {
        method: "POST",
        body: JSON.stringify({ sessionId, question: safeQuestion }),
      });
      return { value: envelope.data, source: apiSource(envelope.source) };
    } catch {
      return { value: await localMock.analyzeQuestion(safeQuestion), source: "local-fallback" };
    }
  }

  async nextQuestion(sessionId: string, topic: Topic, selectedOption: "A" | "B"): Promise<Sourced<NextQuestion>> {
    try {
      const envelope = await requestEnvelope<NextQuestion>("/api/answer", {
        method: "POST",
        body: JSON.stringify({ sessionId, stage: 1, selectedOption, topic }),
      });
      return { value: envelope.data, source: apiSource(envelope.source) };
    } catch {
      return { value: await localMock.generateNextQuestion({ topic, selectedOption }), source: "local-fallback" };
    }
  }

  async reflection(input: {
    sessionId: string;
    originalQuestion: string;
    firstAnswer: string;
    secondAnswer: string;
    thirdAnswer: string;
  }): Promise<Sourced<ReflectionResult>> {
    try {
      const envelope = await requestEnvelope<ReflectionResult>("/api/result", {
        method: "POST",
        body: JSON.stringify(input),
      });
      return { value: envelope.data, source: apiSource(envelope.source) };
    } catch {
      return {
        value: await localMock.generateReflection(input),
        source: "local-fallback",
      };
    }
  }

  async refineAction(sessionId: string, original: string): Promise<Sourced<ActionResult>> {
    const safeOriginal = cleanUserText(original);
    try {
      const envelope = await requestEnvelope<ActionResult>("/api/action", {
        method: "POST",
        body: JSON.stringify({ sessionId, original: safeOriginal }),
      });
      return { value: { ...envelope.data, original: safeOriginal }, source: apiSource(envelope.source) };
    } catch {
      return { value: await localMock.refineAction(safeOriginal), source: "local-fallback" };
    }
  }

  async complete(input: {
    sessionId: string;
    topic: Topic;
    actionKeyword: string;
    caseId: string | null;
  }): Promise<Sourced<PublicStats>> {
    try {
      const envelope = await requestEnvelope<PublicStats>("/api/complete", {
        method: "POST",
        body: JSON.stringify({ ...input, journeyCompleted: true }),
      });
      return { value: envelope.data, source: apiSource(envelope.source) };
    } catch {
      const key = "red-boat-local-completed";
      const count = Number(window.localStorage.getItem(key) ?? "0") + 1;
      window.localStorage.setItem(key, String(count));
      return { value: { completed: count, updatedAt: new Date().toISOString() }, source: "local-fallback" };
    }
  }

  /**
   * 第七页「三次回望」：把这一步的回答交给后端 AI，换回一段针对这一步的回望。
   * 卡片原文只用于本地兜底（服务端会自己从审核内容库取同一张卡）。
   */
  async recall(input: RecallRequest & { coordinateTitle: string; coordinateContent: string; coordinateQuote?: string | null }): Promise<Sourced<RecallCoordinate>> {
    const { coordinateTitle, coordinateContent, coordinateQuote, ...payload } = input;
    try {
      const envelope = await requestEnvelope<RecallCoordinate>("/api/recall", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return { value: envelope.data, source: apiSource(envelope.source) };
    } catch {
      return {
        value: fallbackRecallCoordinate({
          ...payload,
          label: RECALL_LABELS[payload.step],
          focus: recallFocus(payload),
          coordinateTitle,
          coordinateContent,
          coordinateQuote: coordinateQuote ?? "",
        }),
        source: "local-fallback",
      };
    }
  }

  async publishedKnowledge(theme?: string, throwOnError = false): Promise<KnowledgeCard[]> {
    try {
      return await request<KnowledgeCard[]>(`/api/public/knowledge${theme ? `?theme=${encodeURIComponent(theme)}` : ""}`, {
        method: "GET",
      });
    } catch (error) {
      if (throwOnError) throw error;
      return [];
    }
  }

  async publishedCase(theme: string, context = ""): Promise<JiaxingCase | null> {
    try {
      const result = await request<JiaxingCase[]>(`/api/public/cases?theme=${encodeURIComponent(theme)}`, { method: "GET" });
      if (!result.length) return null;
      const hash = Array.from(context).reduce((value, character) => ((value * 31) + (character.codePointAt(0) ?? 0)) >>> 0, 2166136261);
      return result[hash % result.length] ?? result[0];
    } catch {
      return null;
    }
  }

  async publicStats(): Promise<PublicStats> {
    try {
      return await request<PublicStats>("/api/public/stats", { method: "GET" });
    } catch {
      return { completed: Number(window.localStorage.getItem("red-boat-local-completed") ?? "0"), updatedAt: new Date().toISOString() };
    }
  }
}
